import React, { useEffect, useState } from "react";
import { ItemFieldTypes } from "../../../../forms.interface";
import {
	FilterOperators,
	GetSelectOptionsSortByNames,
	ItemFieldFragment,
	useGetSelectOptionsQuery,
} from "../../../../../../generated/nuggetKmsApi/graphql";
import "./itemField.scss";
import { Constants } from "../../../../../../constants/Constants";
import { toast } from "react-toastify";
import ParseHTMLContent from "../../../../../../components/ParseHTMLContent/ParseHTMLContent";

interface ItemFieldProps {
	itemField: ItemFieldFragment;
	value: string | null;
	onChange: (value: string) => void;
}

export const ItemField: React.FC<ItemFieldProps> = ({ itemField, value, onChange }) => {
	const [localValue, setLocalValue] = useState<string>(value || "");

	// Fetch select options if this field uses a select
	const {
		data: selectOptionsData,
		error: selectOptionsError,
		loading: selectOptionsLoading,
	} = useGetSelectOptionsQuery({
		variables: {
			input: {
				filter: {
					fields: {
						selectId: {
							val: itemField.selectId || 0,
							op: FilterOperators.Equal,
						},
					},
				},
				sortBys: [
					{
						name: GetSelectOptionsSortByNames.SortOrder,
						isDesc: false,
					},
				],
			},
		},
		skip: !itemField.selectId,
		context: {
			clientName: Constants.APOLLO_CLIENT_NUGGET_KMS_API_NAME,
		},
	});

	const selectOptions = selectOptionsData?.getSelectOptions.rows || [];

	useEffect(() => {
		setLocalValue(value || "");
	}, [value]);

	// Show toast error when select options fail to load
	useEffect(() => {
		if (selectOptionsError) {
			toast.error("Failed to load options for this field. Please refresh the page.");
		}
	}, [selectOptionsError]);

	const handleChange = (newValue: string) => {
		setLocalValue(newValue);
		onChange(newValue);
	};

	// Render label if field has a name
	const renderLabel = () => {
		if (itemField.name) {
			return (
				<label htmlFor={itemField.name} className="item-field-label">
					<ParseHTMLContent htmlString={itemField.name} />
					{itemField.isRequired && <span className="item-field-required">*</span>}
					{itemField.subtitle && (
						<span className="item-field-subtitle">
							{" "}
							(<ParseHTMLContent htmlString={itemField.subtitle} />)
						</span>
					)}
				</label>
			);
		}
		return null;
	};

	// Render description if exists
	const renderDescription = () => {
		if (itemField.description) {
			return (
				<p className="item-field-description">
					<ParseHTMLContent htmlString={itemField.description} />
				</p>
			);
		}
		return null;
	};

	switch (itemField.type.code) {
		case ItemFieldTypes.TEXT_INPUT:
			return (
				<div className="item-field">
					{renderLabel()}
					{renderDescription()}
					<input
						name={itemField.name ?? undefined}
						type="text"
						className="item-field-input"
						value={localValue}
						onChange={(e) => handleChange(e.target.value)}
						required={!!itemField.isRequired}
						placeholder={itemField.placeholder || "Enter your answer"}
					/>
				</div>
			);

		case ItemFieldTypes.INT_INPUT:
			return (
				<div className="item-field">
					{renderLabel()}
					{renderDescription()}
					<input
						name={itemField.name ?? undefined}
						type="number"
						className="item-field-input"
						value={localValue}
						onChange={(e) => handleChange(e.target.value)}
						placeholder={itemField.placeholder || "Enter a number"}
						step="1"
						required={!!itemField.isRequired}
					/>
				</div>
			);

		case ItemFieldTypes.FLOAT_INPUT:
			return (
				<div className="item-field">
					{renderLabel()}
					{renderDescription()}
					<input
						name={itemField.name ?? undefined}
						type="number"
						className="item-field-input"
						value={localValue}
						onChange={(e) => handleChange(e.target.value)}
						placeholder={itemField.placeholder || "Enter a decimal number"}
						step="0.01"
						required={!!itemField.isRequired}
					/>
				</div>
			);

		case ItemFieldTypes.PERCENTAGE_INPUT:
			return (
				<div className="item-field">
					{renderLabel()}
					{renderDescription()}
					<div className="item-field-input-group">
						<input
							name={itemField.name ?? undefined}
							type="number"
							className="item-field-input"
							value={localValue}
							onChange={(e) => handleChange(e.target.value)}
							placeholder={itemField.placeholder || "Enter percentage"}
							step="0.1"
							min="0"
							max="100"
							required={!!itemField.isRequired}
						/>
						<span className="item-field-addon">%</span>
					</div>
				</div>
			);

		case ItemFieldTypes.CURRENCY_INPUT:
			return (
				<div className="item-field">
					{renderLabel()}
					{renderDescription()}
					<div className="item-field-input-group">
						<span className="item-field-addon">$</span>
						<input
							name={itemField.name ?? undefined}
							type="number"
							className="item-field-input"
							value={localValue}
							onChange={(e) => handleChange(e.target.value)}
							placeholder={itemField.placeholder || "0.00"}
							step="0.01"
							min="0"
							required={!!itemField.isRequired}
						/>
					</div>
				</div>
			);

		case ItemFieldTypes.TEXT_AREA:
			return (
				<div className="item-field">
					{renderLabel()}
					{renderDescription()}
					<textarea
						name={itemField.name ?? undefined}
						className="item-field-textarea"
						value={localValue}
						onChange={(e) => handleChange(e.target.value)}
						placeholder={itemField.placeholder || "Enter your answer"}
						rows={4}
						required={!!itemField.isRequired}
					/>
				</div>
			);

		case ItemFieldTypes.DATE:
			return (
				<div className="item-field">
					{renderLabel()}
					{renderDescription()}
					<input
						name={itemField.name ?? undefined}
						type="date"
						className="item-field-input"
						value={localValue}
						onChange={(e) => handleChange(e.target.value)}
						required={!!itemField.isRequired}
					/>
				</div>
			);

		case ItemFieldTypes.SELECT:
			return (
				<div className="item-field">
					{renderLabel()}
					{renderDescription()}
					<select
						name={itemField.name ?? undefined}
						className="item-field-select"
						value={localValue}
						onChange={(e) => handleChange(e.target.value)}
						required={!!itemField.isRequired}
						disabled={selectOptionsLoading}
					>
						<option value="">
							{selectOptionsLoading
								? "Loading options..."
								: selectOptionsError
								? "Error loading options"
								: itemField.placeholder || "Select an option"}
						</option>
						{selectOptions.map((option) => (
							<option key={option.id} value={option.name}>
								{option.name}
							</option>
						))}
					</select>
				</div>
			);

		case ItemFieldTypes.MULTISELECT:
			return (
				<div className="item-field">
					{renderLabel()}
					{renderDescription()}
					{selectOptionsLoading ? (
						<p className="item-field-hint">Loading options...</p>
					) : selectOptionsError ? (
						<p className="item-field-error">Error loading options. Please refresh the page.</p>
					) : (
						<>
							<select
								name={itemField.name ?? undefined}
								className="item-field-select"
								multiple
								value={localValue ? localValue.split(",") : []}
								onChange={(e) => {
									const selectedValues = Array.from(
										e.target.selectedOptions,
										(option) => option.value,
									);
									handleChange(selectedValues.join(","));
								}}
								required={!!itemField.isRequired}
							>
								{selectOptions.map((option) => (
									<option key={option.id} value={option.name}>
										{option.name}
									</option>
								))}
							</select>
							<p className="item-field-hint">Hold Ctrl (or Cmd) to select multiple options</p>
						</>
					)}
				</div>
			);

		case ItemFieldTypes.RADIO:
			return (
				<div className="item-field">
					{renderLabel()}
					{renderDescription()}
					{selectOptionsLoading ? (
						<p className="item-field-hint">Loading options...</p>
					) : selectOptionsError ? (
						<p className="item-field-error">Error loading options. Please refresh the page.</p>
					) : (
						<div className="item-field-radio-group">
							{selectOptions.map((option) => (
								<label key={option.id} className="item-field-radio-option">
									<input
										type="radio"
										name={`radio-${itemField.id}`}
										value={option.name}
										checked={localValue === option.name}
										onChange={(e) => handleChange(e.target.value)}
										required={!!itemField.isRequired}
									/>
									<span className="item-field-radio-label">{option.name}</span>
									{option.description && (
										<span className="item-field-option-description">
											<ParseHTMLContent htmlString={option.description} />
										</span>
									)}
								</label>
							))}
						</div>
					)}
				</div>
			);

		case ItemFieldTypes.CHECKBOX:
			return (
				<div className="item-field">
					{renderLabel()}
					{renderDescription()}
					<label htmlFor={itemField.name ?? undefined} className="item-field-checkbox">
						<input
							name={itemField.name ?? undefined}
							type="checkbox"
							checked={localValue === "true" || localValue === "1"}
							onChange={(e) => handleChange(e.target.checked ? "true" : "false")}
							required={!!itemField.isRequired}
						/>
						<span className="item-field-checkbox-label">
							{itemField.placeholder || "Check to confirm"}
						</span>
					</label>
				</div>
			);

		default:
			return (
				<div className="item-field">
					{renderLabel()}
					{renderDescription()}
					<p className="item-field-error">
						Unsupported field type: {itemField.type.code} ({itemField.type.name})
					</p>
				</div>
			);
	}
};
